import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight, FiArrowLeft, FiShoppingBag, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { RiDoubleQuotesL, RiDoubleQuotesR } from 'react-icons/ri';
import { userApi } from '../../api/userApi';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const [heroSlides, setHeroSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true);
  const navigate = useNavigate();
  const [youtubeVideo, setYoutubeVideo] = useState(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [indexBanner, setIndexBanner] = useState(null);
  const [isBannerLoading, setIsBannerLoading] = useState(true);

  useEffect(() => {
    const fetchCarousels = async () => {
      try {
        setIsLoading(true);
        const data = await userApi.getActiveCarousels();
        const formattedSlides = data.map(slide => ({
          image: slide.image,
          link: getSlideLink({
            link_type: slide.link_type,
            brand_name: slide.brand_details?.name, // Updated to use brand_details
            category_name: slide.category_details?.name, // Updated to use category_details
          }),
          title: slide.title,
        }));
        setHeroSlides(formattedSlides);
      } catch (error) {
        console.error('Failed to fetch carousels:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCarousels();
  }, []);

  const getSlideLink = (slide) => {
    switch (slide.link_type) {
      case 'brand':
        return `/products?brand=${encodeURIComponent(slide.brand_name)}`;
      case 'category':
        return `/products?category=${encodeURIComponent(slide.category_name)}`;
      default:
        return '/products';
    }
  };

  useEffect(() => {
    if (heroSlides.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === heroSlides.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [heroSlides]);

  const nextSlide = () => {
    setDirection(1);
    setCurrentSlide((prev) => (prev === heroSlides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrentSlide((prev) => (prev === 0 ? heroSlides.length - 1 : prev - 1));
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsCategoriesLoading(true);
        const data = await userApi.getCategories();
        // Filter only active categories
        const activeCategories = data.filter(category => category.is_active);
        
        // Split into top and bottom rows
        const topRowCategories = activeCategories.slice(0, 4);
        const bottomRowCategories = activeCategories.slice(4, 8);

        setCategories({
          topRow: topRowCategories.map(category => ({
            name: category.name,
            image: category.image,
            link: `/products?category=${encodeURIComponent(category.name)}`
          })),
          bottomRow: bottomRowCategories.map(category => ({
            name: category.name,
            image: category.image,
            link: `/products?category=${encodeURIComponent(category.name)}`
          }))
        });
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setIsCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Add this function to extract video ID from URL
  const getYoutubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Add this effect for fetching YouTube video
  useEffect(() => {
    const fetchYoutubeVideo = async () => {
      try {
        setIsVideoLoading(true);
        const data = await userApi.getYoutubeVideo();
        setYoutubeVideo(data);
      } catch (error) {
        console.error('Failed to fetch YouTube video:', error);
      } finally {
        setIsVideoLoading(false);
      }
    };

    fetchYoutubeVideo();
  }, []);

  // Add this effect for fetching the index banner
  useEffect(() => {
    const fetchIndexBanner = async () => {
      try {
        setIsBannerLoading(true);
        const data = await userApi.getIndexBanner();
        setIndexBanner(data);
      } catch (error) {
        console.error('Failed to fetch index banner:', error);
      } finally {
        setIsBannerLoading(false);
      }
    };

    fetchIndexBanner();
  }, []);

  if (isLoading) {
    return (
      <div className="h-[80vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Panchali Boutique - Luxury Indian Fashion & Designer Wear</title>
        <meta name="description" content="Discover exclusive designer wear, traditional sarees, and luxury Indian fashion at Panchali Boutique. Shop our latest collection of ethnic wear." />
      </Helmet>

      {/* Hero Carousel Section */}
      {heroSlides.length > 0 && (
        <section className="relative h-[50vh] sm:h-[70vh] md:h-[90vh] w-full overflow-hidden mt-0">
          {/* Slides */}
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentSlide}
              custom={direction}
              initial={{ 
                opacity: 0,
                x: direction > 0 ? 1000 : -1000,
              }}
              animate={{ 
                opacity: 1,
                x: 0,
                transition: {
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }
              }}
              exit={{ 
                opacity: 0,
                x: direction > 0 ? -1000 : 1000,
                transition: {
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }
              }}
              className="absolute inset-0 cursor-pointer"
              onClick={() => navigate(heroSlides[currentSlide].link)}
            >
              <motion.div 
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${heroSlides[currentSlide].image})`,
                }}
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.5 }}
              >
                <div className="absolute inset-0 bg-black/20"></div>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Controls */}
          {heroSlides.length > 1 && (
            <>
              {/* Navigation Arrows */}
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-between px-4 sm:px-6 lg:px-8">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={prevSlide}
                  className="bg-white/10 backdrop-blur-md text-white p-4 rounded-full hover:bg-white/20 transition-colors group"
                >
                  <FiArrowLeft className="w-6 h-6 group-hover:scale-110 transition-transform" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={nextSlide}
                  className="bg-white/10 backdrop-blur-md text-white p-4 rounded-full hover:bg-white/20 transition-colors group"
                >
                  <FiArrowRight className="w-6 h-6 group-hover:scale-110 transition-transform" />
                </motion.button>
              </div>

              {/* Slide Indicators */}
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-4">
                {heroSlides.map((_, index) => (
                  <motion.button
                    key={index}
                    onClick={() => {
                      setDirection(index > currentSlide ? 1 : -1);
                      setCurrentSlide(index);
                    }}
                    className={`relative h-2 rounded-full transition-all duration-300 ${
                      currentSlide === index ? 'w-8 bg-white' : 'w-2 bg-white/50'
                    }`}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {currentSlide === index && (
                      <motion.div
                        layoutId="activeIndicator"
                        className="absolute inset-0 bg-white rounded-full"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                  </motion.button>
                ))}
              </div>

              {/* Progress Bar */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                exit={{ scaleX: 0 }}
                transition={{ duration: 5, ease: "linear" }}
                key={currentSlide}
                className="absolute bottom-0 left-0 right-0 h-1 bg-white/30 origin-left"
              />
            </>
          )}
        </section>
      )}

      {/* Luxury Quote Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute left-0 top-0 w-1/3 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent opacity-70"></div>
        <div className="absolute right-0 bottom-0 w-1/3 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent opacity-70"></div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto px-4 text-center relative"
        >
          <div className="absolute -left-4 top-0 text-5xl text-gray-200 opacity-50">
            <RiDoubleQuotesL />
          </div>
          <div className="absolute -right-4 bottom-0 text-5xl text-gray-200 opacity-50">
            <RiDoubleQuotesR />
          </div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="space-y-6"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-800 leading-relaxed tracking-wide">
              Where every thread tells a story of{' '}
              <span className="font-medium italic">tradition</span>, every stitch embodies{' '}
              <span className="font-medium italic">luxury</span>, and every design celebrates{' '}
              <span className="font-medium italic">timeless elegance</span>
            </h2>
            
            <div className="flex flex-col items-center space-y-3">
              <div className="w-16 h-[2px] bg-gradient-to-r from-indigo-500 to-purple-500"></div>
              <p className="text-lg text-gray-600 tracking-widest uppercase">Panchali Boutique</p>
            </div>
          </motion.div>
        </motion.div>

        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 -z-10">
          <div className="h-32 w-32 rounded-full bg-gradient-to-r from-purple-50 to-pink-50 blur-3xl opacity-70 mx-auto"></div>
        </div>
      </section>

      {/* Latest Arrivals Section */}
      <section className="relative py-24 bg-[#FAF9F6]">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <LatestArrivals />
      </section>

      {/* Categories Section */}
      <section className="py-12 w-full overflow-hidden">
        {isCategoriesLoading ? (
          <div className="h-40 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <div className="w-full space-y-12 py-12">
            {/* First Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-12">
              {categories.topRow?.map((category, index) => (
                <motion.div
                  key={`top-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="relative flex flex-col items-center"
                >
                  <Link 
                    to={category.link}
                    className="block w-full"
                  >
                    <div className="flex flex-col">
                      <div className="relative h-[600px] sm:h-[800px] lg:h-[1000px] xl:h-[1000px] overflow-hidden">
                        <img
                          src={category.image}
                          alt={category.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="mt-3 text-center">
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-light text-gray-800">
                          {category.name}
                        </h3>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Second Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-12">
              {categories.bottomRow?.map((category, index) => (
                <motion.div
                  key={`bottom-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="relative flex flex-col items-center"
                >
                  <Link 
                    to={category.link}
                    className="block w-full"
                  >
                    <div className="flex flex-col">
                      <div className="relative h-[600px] sm:h-[800px] lg:h-[1000px] xl:h-[1000px] overflow-hidden">
                        <img
                          src={category.image}
                          alt={category.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="mt-3 text-center">
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-light text-gray-800">
                          {category.name}
                        </h3>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Banner Section */}
      <section className="w-full mb-12">
        <div className="relative h-[400px] md:h-[750px] w-full overflow-hidden">
          {isBannerLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2C3E50] border-t-transparent"></div>
            </div>
          ) : indexBanner ? (
            <img 
              src={indexBanner.banner_image}
              alt="Panchali Banner"
              className="w-full h-full object-contain md:object-fill"
            />
          ) : (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
              <p className="text-gray-500">No banner available</p>
            </div>
          )}
        </div>
      </section>

      {/* Behind The Scenes Section */}
      <section className="relative py-24 bg-[#FAF9F6]">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-light mb-4">Behind The Scenes</h2>
            <div className="w-24 h-[2px] bg-gradient-to-r from-indigo-500 to-purple-500 mx-auto"></div>
          </motion.div>

          {/* Original Video Section - Unchanged */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative overflow-hidden bg-white shadow-sm hover:shadow-xl transition-shadow duration-300">
              {isVideoLoading ? (
                <div className="flex justify-center items-center h-96">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2C3E50] border-t-transparent"></div>
                </div>
              ) : youtubeVideo ? (
                <div className="aspect-video w-full">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${getYoutubeVideoId(youtubeVideo.video_url)}`}
                    title="Behind The Scenes at Panchali Boutique"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <div className="flex justify-center items-center h-96 bg-gray-50">
                  <p className="text-gray-500">Video coming soon</p>
                </div>
              )}

            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

const LatestArrivals = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const checkScrollButtons = () => {
    const container = scrollRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  const scroll = (direction) => {
    const container = scrollRef.current;
    if (container) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const fetchLatestArrivals = async () => {
      try {
        setIsLoading(true);
        const data = await userApi.getNewArrivals();
        
        const transformedProducts = data
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // Sort by creation date, newest first
          .slice(0, 6) // Take only the first 6 items
          .map(product => ({
            id: product.id,
            name: product.name,
            price: product.price,
            salePrice: product.sale_price,
            finalPrice: product.final_price,
            image: product.variants[0]?.images[0]?.images || '',
            category: product.category.name,
            activeOffers: product.active_offers || []
          }));

        setProducts(transformedProducts);
      } catch (error) {
        console.error('Error fetching latest arrivals:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLatestArrivals();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2C3E50] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Section Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <h2 className="text-3xl font-light mb-4">Latest Arrivals</h2>
        <div className="w-24 h-[2px] bg-gradient-to-r from-indigo-500 to-purple-500 mx-auto"></div>
      </motion.div>

      {/* Scrollable Container with Navigation Arrows */}
      <div className="relative">
        {/* Left Navigation Arrow */}
        <motion.button
          initial={false}
          animate={{ opacity: canScrollLeft ? 1 : 0 }}
          className={`absolute left-0 top-[calc(50%-28px)] z-10
            -translate-x-5
            bg-white/90 backdrop-blur-sm text-gray-800 
            p-3 rounded-full shadow-lg hover:bg-white 
            transition-all duration-200
            ${canScrollLeft ? 'visible' : 'invisible'}
            flex items-center justify-center`}
          onClick={() => scroll('left')}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FiChevronLeft className="w-6 h-6" />
        </motion.button>

        {/* Scrollable Content */}
        <div 
          ref={scrollRef}
          className="overflow-x-auto hide-scrollbar scroll-smooth"
          onScroll={checkScrollButtons}
        >
          <div className="inline-flex gap-6 pb-6">
            {products.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex-none w-[280px] sm:w-[320px] md:w-96 cursor-pointer"
                onClick={() => handleProductClick(item.id)}
              >
                <div className="relative overflow-hidden bg-white shadow-sm hover:shadow-xl transition-shadow duration-300 group">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="text-lg font-medium mb-1">{item.name}</h3>
                      <p className="text-sm opacity-90">{item.category}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {item.activeOffers?.length > 0 ? (
                          <>
                            <span className="text-lg font-semibold">₹{item.finalPrice?.toLocaleString()}</span>
                            <span className="text-sm line-through opacity-75">₹{item.price?.toLocaleString()}</span>
                            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded">
                              {item.activeOffers[0].offer_type === 'PERCENTAGE' 
                                ? `${item.activeOffers[0].discount_value}% OFF`
                                : `₹${item.activeOffers[0].discount_value} OFF`
                              }
                            </span>
                          </>
                        ) : (
                          <span className="text-lg font-semibold">
                            ₹{(item.salePrice || item.price)?.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Navigation Arrow */}
        <motion.button
          initial={false}
          animate={{ opacity: canScrollRight ? 1 : 0 }}
          className={`absolute right-0 top-[calc(50%-28px)] z-10
            translate-x-5
            bg-white/90 backdrop-blur-sm text-gray-800 
            p-3 rounded-full shadow-lg hover:bg-white 
            transition-all duration-200
            ${canScrollRight ? 'visible' : 'invisible'}
            flex items-center justify-center`}
          onClick={() => scroll('right')}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FiChevronRight className="w-6 h-6" />
        </motion.button>
      </div>
    </div>
  );
};

const categoryData = {
  topRow: [
    {
      name: "Skirts and Lehnga set",
      image: "https://i.pinimg.com/736x/34/88/a1/3488a153b2b633674a324d3ae8038165.jpg",
      link: "/products?category=Lehengas"
    },
    {
      name: "Saris",
      image: "https://i.pinimg.com/736x/c9/a3/52/c9a352a9f1f554a2010bd03e4f91a704.jpg",
      link: "/products?category=Sarees"
    },
    {
      name: "Designer Suits",
      image: "https://i.pinimg.com/736x/62/f3/67/62f3674e75a2bac7de214b5aa80714ca.jpg",
      link: "/products?category=Suits"
    },
    {
      name: "Bridal Collection",
      image: "https://i.pinimg.com/736x/69/36/f4/6936f47c0bdbd819fe9bd91ffd796df5.jpg",
      link: "/products?filter=bridal"
    }
  ],
  bottomRow: [
    {
      name: "Indo Western",
      image: "https://i.pinimg.com/736x/62/8b/91/628b91608a1d96ea4b67da721a88de50.jpg",
      link: "/products?category=Indo-Western"
    },
    {
      name: "Gowns",
      image: "https://i.pinimg.com/736x/62/8b/91/628b91608a1d96ea4b67da721a88de50.jpg",
      link: "/products?category=Gowns"
    },
    {
      name: "Traditional Kurtis",
      image: "https://i.pinimg.com/736x/69/36/f4/6936f47c0bdbd819fe9bd91ffd796df5.jpg",
      link: "/products?category=Kurtis"
    },
    {
      name: "Designer Blouses",
      image: "https://i.pinimg.com/736x/69/36/f4/6936f47c0bdbd819fe9bd91ffd796df5.jpg",
      link: "/products?category=Blouses"
    }
  ]
};

export default Home;
















































